import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useAuth } from '@/auth/AuthProvider';
import { useAgency } from '@/auth/AgencyProvider';
import { userChannel } from '@/lib/channels';
import { parseChatHint, type ChatHint } from '@/lib/chat';
import { pollDelay } from '@/lib/realtime';
import { useRealtime } from './RealtimeProvider';

/**
 * The chat's ALWAYS-MOUNTED consumer (design 7.4; memory push-traps: a type with a socket channel needs a
 * consumer that is mounted for the whole window of the event, not only while its screen is open).
 *
 * Mounted once in the shell next to the rejection notices. It listens on `user:<self>` (every office
 * session already subscribes there; the relay fans each chat hint out to the thread's office readers),
 * keeps the badge (`chat_unread_total`) and hands every hint to the chat screens that registered, so the
 * inbox and an open thread re-read. A driver message on a thread that is not on screen raises a toast.
 * The fallback poll (120 s live, 20 s otherwise, none while hidden) and every (re)subscribe re-read the
 * badge: a first subscription recovers nothing (memory first-subscription-no-recovery).
 *
 * Accounting is not in the chat (Hector, decision 2): for that role nothing is read or subscribed.
 */

export interface ChatUnread {
  /** False for accounting (and before the agency is ready): the chat is not offered at all. */
  enabled: boolean;
  unreadThreads: number;
  unreadMessages: number;
  /** Re-read the badge now (after a mark-read, for instance). */
  reload(): void;
  /** Receive every chat hint on the office's own channel; returns the unsubscribe. */
  onHint(cb: (hint: ChatHint) => void): () => void;
  /** The thread on screen (null when none): its driver messages do not toast. */
  setOpenThread(threadId: string | null): void;
}

const ChatUnreadContext = React.createContext<ChatUnread | null>(null);

const BADGE_DEBOUNCE_MS = 300;

function isVisible(): boolean {
  return typeof document === 'undefined' || document.visibilityState !== 'hidden';
}

export function ChatUnreadProvider({ children }: { children: React.ReactNode }) {
  const { client, user } = useAuth();
  const agency = useAgency();
  const { state, subscribe } = useRealtime();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const agencyId = agency.status === 'ready' ? agency.agency.id : null;
  const enabled = agency.status === 'ready' && agency.role !== 'accounting';
  const userId = user?.id ?? null;

  const [counts, setCounts] = React.useState({ threads: 0, messages: 0 });
  const listeners = React.useRef(new Set<(h: ChatHint) => void>());
  const openThread = React.useRef<string | null>(null);
  const debounce = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const readTotal = React.useCallback(async () => {
    if (!enabled || !agencyId) return;
    const { data, error } = await client.rpc('chat_unread_total', { p_agency: agencyId });
    if (error) return;
    const row = (Array.isArray(data) ? data[0] : data) as { unread_messages?: number; unread_threads?: number } | null;
    setCounts({ threads: Number(row?.unread_threads ?? 0), messages: Number(row?.unread_messages ?? 0) });
  }, [client, agencyId, enabled]);

  const readTotalRef = React.useRef(readTotal);
  readTotalRef.current = readTotal;

  const reload = React.useCallback(() => {
    if (debounce.current) clearTimeout(debounce.current);
    debounce.current = setTimeout(() => {
      debounce.current = null;
      void readTotalRef.current();
    }, BADGE_DEBOUNCE_MS);
  }, []);

  React.useEffect(() => {
    if (!enabled) {
      setCounts({ threads: 0, messages: 0 });
      return;
    }
    void readTotal();
  }, [enabled, readTotal]);

  React.useEffect(
    () => () => {
      if (debounce.current) clearTimeout(debounce.current);
    },
    [],
  );

  const toastFor = React.useCallback(
    async (hint: Extract<ChatHint, { type: 'chat.message' }>) => {
      if (!agencyId) return;
      const { data } = await client.from('drivers').select('name').eq('agency_id', agencyId).eq('id', hint.driverId).maybeSingle();
      const name = (data as { name?: string } | null)?.name;
      if (!name) return; // not a driver this agency reads
      toast(t('chat.toast.newMessage', { driver: name }), {
        id: `chat:${hint.threadId}`,
        action: { label: t('chat.toast.open'), onClick: () => navigate(`/chat/${hint.threadId}`) },
      });
    },
    [client, agencyId, navigate, t],
  );
  const toastRef = React.useRef(toastFor);
  toastRef.current = toastFor;

  // user:<self>, while the chat is offered. A load hint on the same channel parses to null here.
  React.useEffect(() => {
    if (!enabled || !userId) return;
    return subscribe(userChannel(userId), {
      onPublication: (data) => {
        const hint = parseChatHint(data);
        if (!hint) return;
        reload();
        listeners.current.forEach((cb) => cb(hint));
        if (hint.type === 'chat.message' && hint.senderSide === 'driver' && hint.threadId !== openThread.current) {
          void toastRef.current(hint);
        }
      },
      onSubscribed: () => reload(),
      onError: () => console.warn('realtime: user channel refused or failed (chat)'),
    });
  }, [enabled, userId, subscribe, reload]);

  // The fallback poll and the tab coming back.
  const [visible, setVisible] = React.useState(isVisible);
  React.useEffect(() => {
    const onChange = () => {
      const now = isVisible();
      setVisible(now);
      if (now) reload();
    };
    document.addEventListener('visibilitychange', onChange);
    return () => document.removeEventListener('visibilitychange', onChange);
  }, [reload]);
  const delay = enabled ? pollDelay(state, visible) : null;
  React.useEffect(() => {
    if (delay === null) return;
    const id = setInterval(() => void readTotalRef.current(), delay);
    return () => clearInterval(id);
  }, [delay]);

  const value = React.useMemo<ChatUnread>(
    () => ({
      enabled,
      unreadThreads: counts.threads,
      unreadMessages: counts.messages,
      reload,
      onHint: (cb) => {
        listeners.current.add(cb);
        return () => listeners.current.delete(cb);
      },
      setOpenThread: (id) => {
        openThread.current = id;
      },
    }),
    [enabled, counts, reload],
  );
  return <ChatUnreadContext.Provider value={value}>{children}</ChatUnreadContext.Provider>;
}

/** The chat's shell state, or null outside the provider (tests of unrelated screens). */
export function useChatUnread(): ChatUnread | null {
  return React.useContext(ChatUnreadContext);
}
